import { useState, useMemo, Fragment } from "react"
import { formatToDecimalPlaces } from "@/lib/math"

// GraphQL
import { ProjectType, useFinanceDashboardQuery } from "@/api/graphql"

// Router
import { Link } from "@/lib/router"
import { Routes } from "@/constants/routes"

// UI
import { cn } from "@/lib/utils"

import {
	Table,
	TableBody,
	TableDataCell,
	TableHead,
	TableHeading,
	TableRowCell,
} from "@/components/table-controls/TableItems"
import {
	useReactTable,
	getCoreRowModel,
	getPaginationRowModel,
	flexRender,
	createColumnHelper,
} from "@/lib/table"
import { PaginationAsButtons } from "@/components/PaginationAsButtons"

// Translations
import { useTrans } from "@/i18n"
import { useLang } from "@/context/lang"

/*
  OFFLINE = 0  # all the sensors are offline
    CHECK_REQUIRED = 1  # part of the sensors is offline or have malformed data
    OK = 2
    INTEREST_NOT_STARTED = 3
    IN_DEVELOPMENT = 4
	*/
const statusMap: { [key: number]: string } = {
	0: "finance.finance_dashboard.table.status.name.offline",
	1: "finance.finance_dashboard.table.status.name.check_required",
	2: "finance.finance_dashboard.table.status.name.ok",
	3: "finance.finance_dashboard.table.status.name.interest_not_started",
	4: "finance.finance_dashboard.table.status.name.in_development",
}

const statusClassNameMap: { [key: number]: string } = {
	0: "text-red-500",
	1: "text-orange-500",
	2: "text-green-500",
	3: "text-orange-500",
	4: "text-orange-500",
}

const statusIconClassNameMap: { [key: number]: string } = {
	0: "bg-red-500",
	1: "bg-orange-500",
	2: "bg-green-500",
	3: "bg-orange-500",
	4: "bg-orange-500",
}

export /**
 * FinanceProjectsTable
 *
 * @returns
 */
function FinanceProjectsTable() {
	// Constants
	const projectsPerPage = 10

	// State
	const [currentPage, setCurrentPage] = useState<number>(0)

	const t = useTrans("finance")
	const { data, isLoading } = useFinanceDashboardQuery({
		limit: projectsPerPage,
		offset: currentPage * projectsPerPage,
	})
	const { formatNumber } = useLang()

	// Tables
	const columnHelper = createColumnHelper<ProjectType>()
	const columns = useMemo(
		() => [
			columnHelper.accessor("id", {
				id: "project.name",
				header: () => (
					<TableHeading variant="static">
						{t(
							"finance:finance.finance_dashboard.table.project.title",
						)}
					</TableHeading>
				),
			}),
			columnHelper.accessor("overview.monitoring_status.status", {
				id: "status",
				header: () => (
					<TableHeading variant="static">
						{t(
							"finance:finance.finance_dashboard.table.status.title",
						)}
					</TableHeading>
				),
			}),
			columnHelper.accessor("overview.performance_ratio", {
				id: "performance_ratio",
				header: () => (
					<TableHeading variant="static">
						{t(
							"finance:finance.finance_dashboard.table.performance_ratio.title",
						)}
					</TableHeading>
				),
			}),
			columnHelper.accessor("overview.total_production", {
				id: "total_production",
				header: () => (
					<TableHeading variant="static">
						{t(
							"finance:finance.finance_dashboard.table.total_production.title",
						)}
					</TableHeading>
				),
			}),
			columnHelper.accessor("overview.production_delta", {
				id: "production_delta",
				header: () => (
					<TableHeading variant="static">
						{t(
							"finance:finance.finance_dashboard.table.production_delta.title",
						)}
					</TableHeading>
				),
			}),
			columnHelper.accessor("overview.yearly_production_factor", {
				id: "yearly_production_factor",
				header: () => (
					<TableHeading variant="static">
						{t(
							"finance:finance.finance_dashboard.table.yearly_production_factor.title",
						)}
					</TableHeading>
				),
			}),
		],
		[t],
	)
	const table = useReactTable({
		columns,
		data: data?.me?.finance_projects?.results as ProjectType[],
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		state: {
			pagination: {
				pageIndex: 0,
				pageSize: projectsPerPage,
			},
		},
	})

	// Loading state
	if (isLoading) {
		return <FinanceProjectsTableLoading />
	}

	// Data state
	return (
		<>
			<Table>
				<TableHead>
					{table.getHeaderGroups().map((headerGroup) => {
						return (
							<TableRowCell key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									return (
										<Fragment key={header.id}>
											{flexRender(
												header.column.columnDef.header,
												header.getContext(),
											)}
										</Fragment>
									)
								})}
							</TableRowCell>
						)
					})}
				</TableHead>
				<TableBody>
					{data?.me?.finance_projects?.results?.map(
						(project, index) => {
							return (
								<TableRowCell
									key={project?.id}
									isOdd={index % 2 === 0}
									data-testid={`tablerow-${project?.id}`}
								>
									<TableDataCell className="!p-0">
										<Link
											to={`${Routes.FinanceProjects}/${project?.id}`}
											className="block h-full px-6 py-4 font-bold text-gray-700 hover:text-gray-900"
										>
											{project?.name}
										</Link>
									</TableDataCell>
									<TableDataCell
										className={cn(
											"flex items-center gap-2",
											statusClassNameMap[
												project?.overview
													?.monitoring_status
													?.status ?? 0
											],
										)}
									>
										<span
											className={cn(
												"block h-3 w-3 rounded-full",
												statusIconClassNameMap[
													project?.overview
														?.monitoring_status
														?.status ?? 0
												],
											)}
										/>
										{t(
											statusMap[
												project?.overview
													?.monitoring_status
													?.status ?? 0
											],
										)}
										{project?.overview?.monitoring_status
											?.data?.length ? (
											<span>
												(
												{project?.overview?.monitoring_status?.data?.join(
													", ",
												)}
												)
											</span>
										) : null}
									</TableDataCell>
									<TableDataCell
										className={cn(
											// "font-bold",
											// if the performance ratio is over the target,
											// then make it green. otherwise it is under the targeted
											// performance ratio so let's make it red.
											// taken from horizon:
											// horizon/dashboard/app/dashboard/projectStatusTable.model.js:83
											project?.overview
												?.performance_ratio &&
												project?.overview
													?.targeted_performance_ratio
												? project?.overview
														.performance_ratio >
													project?.overview
														.targeted_performance_ratio
													? "text-green-500"
													: "text-red-500"
												: null,
										)}
									>
										{t(
											"finance.finance_dashboard.table.performance_ratio.content",
											{
												value: project?.overview
													?.performance_ratio
													? formatNumber(
															parseFloat(
																project
																	?.overview
																	.performance_ratio,
															),
														)
													: 0,
											},
										)}
									</TableDataCell>
									<TableDataCell>
										{project?.overview?.total_production
											? formatNumber(
													parseFloat(
														project?.overview
															.total_production,
													),
												)
											: 0}
									</TableDataCell>
									<TableDataCell
										className={cn(
											// "font-bold",
											// is the production delta over 100? then it's a good signal
											// so we apply green/positive styles
											// taken from horizon:
											// horizon/dashboard/app/dashboard/projectStatusTable.model.js:104
											project?.overview?.production_delta
												? parseFloat(
														project?.overview
															.production_delta,
													) > 100
													? "text-green-500"
													: "text-red-500"
												: null,
										)}
									>
										{t(
											"finance.finance_dashboard.table.production_delta.content",
											{
												value: project?.overview
													?.production_delta
													? formatNumber(
															parseFloat(
																project
																	?.overview
																	.production_delta,
															),
														)
													: 0,
											},
										)}
									</TableDataCell>
									<TableDataCell>
										{project?.overview
											?.yearly_production_factor
											? formatNumber(
													formatToDecimalPlaces(
														parseFloat(
															project?.overview
																.yearly_production_factor,
														),
													),
												)
											: t(
													"finance.finance_dashboard.table.yearly_production_factor.empty_content",
												)}
									</TableDataCell>
								</TableRowCell>
							)
						},
					)}
				</TableBody>
			</Table>

			<div className="mt-6 flex w-full justify-center px-4 py-5 sm:p-6 md:mt-8">
				<PaginationAsButtons
					countPerPage={projectsPerPage}
					totalCount={data?.me?.finance_projects?.totalCount ?? 0}
					itemType={"common.pagination.item_types.project"}
					currentPage={currentPage + 1}
					currentItemsAmount={
						data?.me?.finance_projects?.results?.length ?? 0
					}
					onNextPage={() =>
						setCurrentPage((currentPage) => currentPage + 1)
					}
					onPrevPage={() =>
						setCurrentPage((currentPage) =>
							Math.max(currentPage - 1, 0),
						)
					}
					analyticsId="investments"
				/>
			</div>
		</>
	)
}

/**
 * FinanceProjectsTable
 * @returns
 */
function FinanceProjectsTableLoading() {
	const t = useTrans("finance")
	const data = [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}]

	return (
		<Table>
			<TableHead>
				<TableRowCell>
					<TableHeading variant="static">
						{t(
							"finance:finance.finance_dashboard.table.project.title",
						)}
					</TableHeading>
					<TableHeading variant="static">
						{t(
							"finance:finance.finance_dashboard.table.status.title",
						)}
					</TableHeading>
					<TableHeading variant="static">
						{t(
							"finance:finance.finance_dashboard.table.performance_ratio.title",
						)}
					</TableHeading>
					<TableHeading variant="static">
						{t(
							"finance:finance.finance_dashboard.table.total_production.title",
						)}
					</TableHeading>
					<TableHeading variant="static">
						{t(
							"finance:finance.finance_dashboard.table.production_delta.title",
						)}
					</TableHeading>
					<TableHeading variant="static">
						{t(
							"finance:finance.finance_dashboard.table.yearly_production_factor.title",
						)}
					</TableHeading>
				</TableRowCell>
			</TableHead>
			<TableBody>
				{data.map((_project, index) => {
					return (
						<TableRowCell
							key={index}
							isOdd={index % 2 === 0}
							data-testid={`tablerow-${index}`}
						>
							<TableDataCell className="!p-0">
								<span className="block h-full px-6 py-4 font-bold text-gray-700 hover:text-gray-900">
									...
								</span>
							</TableDataCell>
							<TableDataCell
								className={cn("flex items-center gap-2")}
							></TableDataCell>
							<TableDataCell></TableDataCell>
							<TableDataCell></TableDataCell>
							<TableDataCell></TableDataCell>
							<TableDataCell></TableDataCell>
						</TableRowCell>
					)
				})}
			</TableBody>
		</Table>
	)
}
